import * as Sentry from '@sentry/react';

/**
 * Categorizes error codes into human-readable error types
 * @param {string} errorCode - The error code (e.g., 'ETIMEDOUT', 'ENOTFOUND')
 * @returns {string} The categorized error type
 */
export const getErrorType = (errorCode) => {
    if (!errorCode) {
        return 'http_error';
    }

    const errorTypeMap = {
        ETIMEDOUT: 'timeout',
        ENOTFOUND: 'dns_error',
        EAI_AGAIN: 'dns_error',
        ECONNREFUSED: 'connection_refused',
        ECONNRESET: 'network_error',
        EPIPE: 'network_error',
        ENETUNREACH: 'network_error',
        EADDRINUSE: 'system_error',
        NETLIFY_TIMEOUT: 'netlify_timeout',
    };

    return errorTypeMap[errorCode] || 'http_error';
};

/**
 * Logs an HTTP error to Sentry with consistent formatting
 * @param {Error} error - The error to log
 * @param {object} options - Options for logging
 * @param {string} [options.level] - Sentry level (e.g., 'warning', 'error'). Defaults to 'error'
 * @param {object} [options.tags] - Additional tags to include
 * @param {object} [options.contexts] - Additional contexts to include
 */
export const logHttpErrorToSentry = (error, options = {}) => {
    const { level, tags = {}, contexts = {} } = options;
    const errorType = getErrorType(error.code);

    const sentryOptions = {
        tags: {
            source: 'httpClient',
            error_type: errorType,
            status_code: error.response?.statusCode,
            error_code: error.code,
            url_host: error.options?.url?.host,
            ...tags,
        },
        contexts: {
            http_request: {
                url: error.options?.url?.href,
                method: error.options?.method,
                timeout: error.options?.timeout?.request,
                retry_limit: error.options?.retry?.limit,
            },
            http_response: {
                status_code: error.response?.statusCode,
                headers: error.response?.headers,
                body: error.response?.body,
            },
            error_details: {
                message: error.message,
                code: error.code,
                name: error.name,
                stack: error.stack,
                // Include any custom context attached to the error
                ...(error.netlifyTimeoutContext || {}),
            },
            ...contexts,
        },
    };

    // Only add level if it's not the default 'error'
    if (level && level !== 'error') {
        sentryOptions.level = level;
    }

    Sentry.captureException(error, sentryOptions);
};

/**
 * Generic error logger for non-HTTP errors
 * @param {Error} error - The error to log
 * @param {object} options - Options for logging
 * @param {string} [options.level] - Sentry level
 * @param {object} [options.tags] - Additional tags
 * @param {object} [options.contexts] - Additional contexts
 */
export const logError = (error, options = {}) => {
    const { level, tags = {}, contexts = {} } = options;

    const sentryOptions = {
        tags,
        contexts,
    };

    if (level && level !== 'error') {
        sentryOptions.level = level;
    }

    Sentry.captureException(error, sentryOptions);
};

/**
 * Adds a breadcrumb to Sentry
 * @param {object} options - Breadcrumb options
 * @param {string} options.message - Breadcrumb message
 * @param {string} [options.category] - Breadcrumb category
 * @param {string} [options.level] - Breadcrumb level (debug, info, warning, error, critical)
 * @param {object} [options.data] - Additional data to attach to the breadcrumb
 */
export const addBreadcrumb = (options = {}) => {
    const { message, category, level = 'info', data = {} } = options;

    Sentry.addBreadcrumb({
        message,
        category,
        level,
        data,
        timestamp: Date.now() / 1000,
    });
};

/**
 * Sets Sentry context
 * @param {string} key - Context key
 * @param {object} context - Context data
 */
export const setContext = (key, context) => {
    Sentry.setContext(key, context);
};
